import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
// reactstrap components
import { Button, Badge } from "reactstrap";
import React, { useCallback, useEffect, useMemo, useState } from "react";
// react component used to create sweet alerts
import ReactBSAlert from "react-bootstrap-sweetalert";
// react plugin for creating notifications over the dashboard
import { ToastContainer } from "react-toastify";
import {
  getVariables,
  deleteVariable,
} from "@/services/variableServices/variableActions";
import { formatDateTime } from "@/utils/formatDateTime";

import VariableDetails from "@/components/dashboard/VariableDetails";
import type { VariableDetailsRecord } from "@/components/dashboard/VariableDetails";
import TableWrapper from "@/utils/TableWrapper";
import NotificationAlertOptions from "@/layouts/Alerts";
import { asCaught } from "@/services/caughtError";

/**
 * Size of the FIRST getVariables request, not a display page size.
 *
 * SCRUM-1112 made getVariables fetch this many, read the total off that
 * response and then issue exactly one follow-up for the remainder. SCRUM-1138
 * moved paging into TableWrapper (fixed 10 per page); reusing that number here
 * would quietly shrink the first request and undo part of SCRUM-1112's tuning,
 * so the two stay separate on purpose.
 */
const INITIAL_FETCH_SIZE = 20;

/** A row of the variables list (GET /api/variables). */
type VariableRow = VariableDetailsRecord & { _id: string; code_name?: string };

/** The cell renderer's argument, reduced to the part these cells read. */
type VariableCellProps = { row: { original: VariableRow } };

export type DisplayVariableProps = {
  handleUpdate: (variable: VariableRow) => void;
};

const DisplayVariable = (props: DisplayVariableProps) => {
  const dispatch = useAppDispatch();
  //states
  // `{}` until a row is picked; the render below tests for that before reading it.
  const [details, setDetails] = useState<VariableRow>({} as VariableRow);
  const [alert, setAlert] = useState<React.ReactNode>(null);
  const [modal, setModal] = useState(false);
  //use selector
  const permission = useCurrentPermission();
  const variables = useAppSelector((state) => state.variables.variables);

  useEffect(() => {
    dispatch(getVariables(INITIAL_FETCH_SIZE));
  }, []);

  const toggle = () => setModal(!modal);

  const handleShowDetails = useCallback((elem: VariableRow) => {
    setDetails(elem);
    setModal((open) => !open);
  }, []);

  const handleUpdateVariable = (elem: VariableRow) => () => {
    props.handleUpdate(elem);
  };

  const notify = (type: string) => {
    NotificationAlertOptions(type, "Variable has been deleted successfully");
  };

  const questionAlert = (variable: VariableRow) => async (e: React.MouseEvent) => {
    e.preventDefault();

    setAlert(
      // @ts-expect-error react-bootstrap-sweetalert prop-types
      <ReactBSAlert
        custom
        style={{ display: "block", marginTop: "-100px" }}
        title="Delete variable Operation"
        customIcon={
          <div
            className="swal2-icon swal2-question swal2-animate-question-icon text-warning"
            style={{ display: "flex" }}
          >
            <span className="swal2-icon-text">?</span>
          </div>
        }
        onConfirm={async () => {
          await setAlert(null);
          try {
            await dispatch(deleteVariable(variable._id));
            notify("success");
          } catch (err) {
            NotificationAlertOptions(
              "error",
              asCaught(err)?.response?.data?.message ||
                "Deleting the variable failed. Please try again."
            );
          }
        }}
        onCancel={() => setAlert(null)}
        confirmBtnBsStyle="warning"
        confirmBtnText="Yes I'm sure"
        btnSize=""
      >
        Are you sure to delete {" " + variable.title + " "} variable
      </ReactBSAlert>
    );
  };

  /**
   * SCRUM-1138: search, sort and pagination come from TableWrapper, so this
   * component only describes the columns. Cells that render a badge, a formatted
   * date or a button have no plain field, so they carry an explicit `id`.
   */
  const columns = useMemo(() => {
    const StatusCell = ({ row }: VariableCellProps) => {
      const { status } = row.original;
      return (
        <Badge
          className="badge-lg"
          color={status === "required" ? "danger" : "primary"}
          pill
        >
          {status ? status.toUpperCase() : "---"}
        </Badge>
      );
    };
    StatusCell.displayName = "VariableStatusCell";

    const VariableTypeCell = ({ row }: VariableCellProps) => {
      const { variable_type: type, variable_complex_type: complex } =
        row.original;
      if (!type) {
        return (
          <Badge className="badge-lg" color="primary" pill>
            ---
          </Badge>
        );
      }
      return (
        <Badge className="badge-lg" color="primary" pill>
          {type !== "Array"
            ? type.toUpperCase()
            : // Unchanged from the JS: an Array row without its element type
              // still throws here; the conversion types it, it does not guard it.
              `${type.toUpperCase()} OF ${complex!.toUpperCase()}`}
        </Badge>
      );
    };
    VariableTypeCell.displayName = "VariableTypeCell";

    const DetailsCell = ({ row }: VariableCellProps) => (
      <Button
        className="mr-4"
        color="primary"
        size="sm"
        onClick={() => handleShowDetails(row.original)}
      >
        view
      </Button>
    );
    DetailsCell.displayName = "VariableDetailsCell";

    const base = [
      {
        accessorKey: "title",
        header: "Label",
        cell: ({ row }) => (
          <span className="name">
            <strong>{row.original.title}</strong>
          </span>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => (
          <strong>
            {row.original.type.split("_").join(" ").toUpperCase()}
          </strong>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: StatusCell,
      },
      {
        id: "variableType",
        accessorKey: "variable_type",
        header: "variable type",
        cell: VariableTypeCell,
      },
      {
        accessorKey: "code_name",
        header: "code name",
        cell: ({ row }) => <strong>{row.original.code_name}</strong>,
      },
      {
        accessorKey: "updatedAt",
        header: "created/updated At",
        cell: ({ row }) => (
          <strong>{formatDateTime(row.original.updatedAt)}</strong>
        ),
      },
      { id: "details", header: "More details", cell: DetailsCell },
    ];

    if (!(permission.publish || permission.edit)) return base;

    const ToolsCell = ({ row }: VariableCellProps) => {
      const element = row.original;
      return (
        <>
          <Button
            className="mr-3"
            size="sm"
            onClick={handleUpdateVariable(element)}
          >
            <i className="ni ni-settings-gear-65" />
          </Button>
          <Button
            className="mr-4"
            color="danger"
            size="sm"
            onClick={questionAlert(element)}
          >
            <i className="fas fa-trash" />
          </Button>
        </>
      );
    };
    ToolsCell.displayName = "VariableToolsCell";

    return [...base, { id: "tools", header: "Tools", cell: ToolsCell }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission, handleShowDetails]);

  return (
    <>
      <div className="rna-wrapper">
        <ToastContainer />
      </div>
      {alert}

      <TableWrapper data={variables} columns={columns} />

      {Object.keys(details).length === 0 ? null : (
        <VariableDetails details={details} modal={modal} toggle={toggle} />
      )}
    </>
  );
};
export default DisplayVariable;
